import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useMemo } from 'react';
import { fetchScopedOrgTree, sortTerritorialFirst } from '@/lib/orgTree';
import { queryKeys } from '@/lib/queryKeys';
import { withChosenOptions } from '@/lib/selectOptions';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.5 §1a] THE PICKERS STAY ON SCREEN. A REVERSAL OF [FIX-3b]'s FOURTH CLAIM.
 *
 *  FIX-3b made a chosen source collapse to `CD · H` with the selects behind a click, and
 *  recorded as its own known gap that the reopened cell "collapses again on blur… the one
 *  interaction that could read as the cell not saving". That is what came back as a defect
 *  report: the collapsed text gives no hint it is editable, so an operator who needs to
 *  change a source cannot see how.
 *
 *  ⚠️ AND FIX-3b's WIDTH ARGUMENT DID NOT SURVIVE CONTACT WITH ITS OWN OUTPUT. It collapsed
 *  the cell because "two stacked pickers ate about a fifth of the grid's width" — but the
 *  pickers it shipped are stacked VERTICALLY inside one `<th>`, and that column's measure is
 *  declared on the HEADER (`ForceGrid.tsx`, `w-56`). The collapsed summary sat inside the
 *  same 224px. Collapsing bought ZERO horizontal pixels.
 *
 *  What it bought is ROW HEIGHT — a source row goes from one 30px control back to two plus a
 *  gap — and that is the honest cost of the reversal rather than something to hide.
 *
 *  ⚠️ THE CODES-AND-TOOLTIP TREATMENT IS NOT GONE, IT IS NOW READ-ONLY-ONLY.
 *  `components/AddresseeCodes.tsx` still renders `CD · H` on the fill sheet, the inbox and
 *  the printed order preview — every surface where there is nothing to open. Its interactive
 *  variant had exactly one consumer, this file, and left with this change (§9).
 *
 *  ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.4 §2] THE DIVISION FIELD OFFERS DIVISIONS. IT USED TO OFFER EVERYTHING.
 *
 *  It was a `TreeSelect` over the whole scoped forest: against the seeded establishment that
 *  is 89 nodes, and nothing stopped an operator picking the Commissionerate itself, or a
 *  police station, as the *division* an allocation is sourced from.
 *
 *  ⚠️ THE FILTER IS `type === 'DIVISION'`, AND IT IS DELIBERATELY NOT A DEPTH TEST.
 *  `identity.constant.ts` is explicit that "`type` is a LABEL on a node, not a statement
 *  about its depth. Nothing in this codebase derives depth from type or vice versa" — and
 *  this does not either. It is a BUSINESS rule about where force may be asked from, which
 *  happens to be spelled with the same word; a division nested deeper tomorrow is still a
 *  division and still a legitimate source, and this keeps offering it.
 *
 *  ⚠️ "DIRECTLY UNDER THE COMMISSIONERATE" IS SATISFIED BY THE TYPE FILTER RATHER THAN BY
 *  A PARENT TEST, AND THAT IS MEASURED RATHER THAN PREFERRED. `GET /org-units/tree` is
 *  scoped server-side, so a DIVISIONAL nodal receives their own subtree — in which the
 *  Commissionerate is ABSENT. A `parent.type === 'COMMISSIONERATE'` test would therefore
 *  filter that nodal's own division out and leave them with an empty picker on the one
 *  screen they use daily. The type filter is right for every scope.
 *
 *  ⚠️ THE DIVISION IS `allowClear`, AND [D4.5 §1d] CHANGED WHY. `lib/surfaces.ts` used to
 *  hide the grid's per-source Remove button and justify that with this prop; D4.5 restored
 *  the button, so the two are now INDEPENDENT recovery routes rather than one propping up
 *  the other. Clearing the division empties a row in place; Remove takes the row away.
 *
 *  ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [FIX-3b] THE CASCADE. Three of its four claims survive intact:
 *
 *   1. THE UNIT IS DISABLED UNTIL A DIVISION IS CHOSEN, and then lists only that division's
 *      descendants. The relationship is visible rather than implied.
 *   2. IT DEFAULTS TO "Any unit". A division-only allocation is the common case and must
 *      stay ONE interaction — choosing a division and stopping is a complete answer.
 *   3. CHANGING THE DIVISION CLEARS THE UNIT. ⚠️ [D4.5 §1b] THAT IS NO LONGER DONE HERE.
 *      This file used to call `onChangeUnit(undefined)` and then `onChangeDivision(next)` in
 *      one handler, and both setters derive from the same `grid` prop — so the second
 *      overwrote the first and the stale unit survived, which is what put a raw ObjectId on
 *      screen. `ForceGrid`'s `setDivision` is now the single writer of both fields.
 *
 *  ⚠️ BOTH SELECTS MATCH ON NAME **AND** CODE. An operator who knows the section code should
 *  not have to remember the station's name to type it. The searchable text is
 *  `"Name (CODE)"` for each, so either spelling finds the row.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export type SourceCellProps = {
  divisionOrgUnitId: string;
  unitOrgUnitId: string | null;
  /**
   * ⚠️ [D4.5 §1b] THE UNITS THE GRID ALREADY RESOLVED — NOW A LABEL FALLBACK, NOT A SUMMARY.
   *
   * It used to feed the collapsed codes, which no longer exist. It is kept, and is more
   * load-bearing than it was: when a stored id is not in the scoped tree at all — an
   * out-of-scope division on an order raised elsewhere — this is the only place a NAME for
   * it can come from. Without it rc-select falls through to `label ?? item.value` and paints
   * the ObjectId.
   */
  orgUnits?: ReadonlyMap<string, { name: string; shortCode: string }>;
  onChangeDivision: (value: string | undefined) => void;
  onChangeUnit: (value: string | undefined) => void;
  disabled?: boolean;
  /**
   * ⚠️ [C6a §2] WHICH ROW THIS CELL IS, FOR ITS ACCESSIBLE NAMES. Optional, and absent is the force
   * grid's `Division` / `Unit` byte for byte. A command role's ask renders one cell per addressee
   * row, several roles per card, so without it every one of them would be announced as "Division".
   */
  ariaContext?: string;
};

type Option = { value: string; label: string };

/**
 * ⚠️ THE LABEL CARRIES THE CODE, which is what makes the search match either spelling — an
 * operator who knows `ESD` should not have to remember "East Suburban Division" to type it.
 */
const optionOf = (unit: { id: string; name: string; shortCode: string }): Option => ({
  value: unit.id,
  label: `${unit.name} (${unit.shortCode})`,
});

/**
 * ⚠️ [D4.5 §1b] THE KEEP-THE-CHOSEN-VALUE RULE LIVES IN `lib/selectOptions.ts` NOW.
 *
 * This file invented it — "the chosen division is kept even if it has since been
 * deactivated" — and the audit found eight other pickers that needed the same guarantee for
 * three different reasons. Read that module's header before touching this; the short version
 * is that rc-select falls through to `label ?? item.value`, so a value with no option paints
 * its own ObjectId.
 *
 * The code goes on the label here, so this wraps the shared helper rather than calling it
 * bare: a fallback built from a name alone would read `Beniapukur PS` beside options that
 * read `Beniapukur PS (BNP)`, and the difference would look like data rather than absence.
 */
const withChosen = (options: readonly Option[], chosenId: string | null, resolve: Resolve): Option[] =>
  withChosenOptions(options, [chosenId], (id) => {
    const known = resolve(id);
    return known ? { name: `${known.name} (${known.shortCode})` } : undefined;
  });

type Resolve = (id: string) => { name: string; shortCode: string } | undefined;

/** "Any unit under this division" — the common case, and a value rather than an absence. */
const ANY_UNIT = '';

export const SourceCell = ({
  divisionOrgUnitId,
  unitOrgUnitId,
  orgUnits,
  onChangeDivision,
  onChangeUnit,
  disabled,
  ariaContext,
}: SourceCellProps) => {
  /* ⚠️ [amended OTHERS-EXPAND] EVERY PAGE, NOT PAGE ONE — a division past row 500 of the tree
     would be missing from the source picker. Shared key; read `fetchScopedOrgTree`'s header. */
  const { data, isFetching } = useQuery({
    queryKey: queryKeys.orgUnits.tree(),
    queryFn: ({ signal }) => fetchScopedOrgTree(signal),
  });

  const units = useMemo(() => data?.items ?? [], [data]);

  /**
   * ⚠️ [D4.5 §1b] THE TREE FIRST, THE GRID'S MAP SECOND, AND THE TWO ARE NOT THE SAME SET.
   *
   * The tree is everything in the caller's SCOPE, so it names a unit the option filter has
   * excluded for a reason of its own — a deactivated one, or one stranded by a division
   * change, which is the case that actually rendered an id. The grid's map is what the PAGE
   * resolved, which reaches ids outside that scope entirely. Neither alone is enough.
   */
  const resolveName = useMemo((): Resolve => {
    const byId = new Map(units.map((unit) => [unit.id, unit]));
    return (id) => byId.get(id) ?? orgUnits?.get(id);
  }, [units, orgUnits]);

  /**
   * ⚠️ [D4.4 §2] DIVISIONS, AND ONLY DIVISIONS. A flat `Select` rather than a `TreeSelect`,
   * because the answer is a flat set — and because the nesting affordance was itself the
   * hole: it invited an operator to open a division and pick a station inside it as though
   * that were the same field.
   *
   * ⚠️ THE CHOSEN DIVISION IS KEPT EVEN IF IT HAS SINCE BEEN DEACTIVATED, or an existing
   * draft would silently lose a source the operator can neither see nor clear. New choices
   * are active-only; what is already on the row still renders.
   */
  const divisionOptions = useMemo(
    () =>
      withChosen(
        /*
         * ⚠️ [amended WINGS.1] THE TERRITORIAL TEN FIRST, AND THE PLACEMENT IS FORCED TWICE
         * OVER. It sorts the filtered UNITS — before `.map(optionOf)`, because an `Option`
         * is `{value,label}` and carries no `subType` for the comparator to read; and
         * before `withChosen`, because `withChosenOptions` appends its fallbacks AT THE END
         * deliberately ("a row nobody may pick in the middle of a list of rows they may"),
         * so sorting afterwards would lift a deactivated or out-of-scope division into the
         * live band. Both halves are one line apart and neither is optional.
         */
        sortTerritorialFirst(
          units.filter((unit) => unit.type === 'DIVISION' && (unit.isActive || unit.id === divisionOrgUnitId)),
        ).map(optionOf),
        divisionOrgUnitId || null,
        resolveName,
      ),
    [units, divisionOrgUnitId, resolveName],
  );

  /**
   * ⚠️ DESCENDANTS BY `ancestors`, NOT BY WALKING THE TREE. Every unit already carries its
   * full ancestor chain (it is what FR-3.6's scoping is built on), so "under this division"
   * is a membership test rather than a traversal — and it stays right at any depth.
   *
   * ⚠️ [D4.5 §1b] AND THE CHOSEN UNIT IS KEPT WHATEVER THE DIVISION SAYS. The old filter was
   * `ancestors.includes(division) && (isActive || id === chosen)`, and the ancestry test is
   * an AND that comes FIRST — so the keep-the-chosen clause protected a DEACTIVATED unit and
   * did nothing at all for a unit stranded by a division change, which is the case that
   * actually rendered an id. `withChosen` closes it for every reason a unit can go missing,
   * including one that is not in the caller's scoped tree at all.
   */
  const unitOptions = useMemo(() => {
    if (!divisionOrgUnitId) return [];
    /*
     * ⚠️ [D4.4 §2] "Any unit" IS AN OPTION, NOT ONLY A PLACEHOLDER. It was reachable only
     * through the `allowClear` ×, so an operator who picked a station and then wanted the
     * whole division back had to discover a control that looks like "cancel". It is the
     * DEFAULT and the common case; it should be the first thing in the list.
     */
    return withChosen(
      [
        { value: ANY_UNIT, label: 'Any unit' },
        ...units
          .filter((unit) => unit.ancestors.includes(divisionOrgUnitId) && (unit.isActive || unit.id === unitOrgUnitId))
          .map(optionOf),
      ],
      unitOrgUnitId,
      resolveName,
    );
  }, [units, divisionOrgUnitId, unitOrgUnitId, resolveName]);

  const chosen = divisionOrgUnitId.length > 0;

  return (
    /*
     * ⚠️ [D4.5 §1a] `w-56 max-w-full`, MATCHING THE COLUMN'S OWN MEASURE. In an auto-layout
     * table a cell width is a MINIMUM, and an unconstrained rc-select grows to its content —
     * so a long division name would widen the whole column, which the truncated summary
     * could not do. Bounded here, the select's own `.ant-select-selection-item` ellipsis
     * takes over. jsdom performs no layout, so this is an `e2e/specs/layout.spec.ts` claim.
     */
    <div className="flex w-56 max-w-full flex-col gap-1" data-source-cell="">
      <span data-testid="source-division">
        <Select
          value={divisionOrgUnitId || undefined}
          /*
           * ⚠️ [D4.5 §1b] IT ONLY REPORTS THE DIVISION. Clearing the unit alongside it used
           * to happen here, as a second call, and `ForceGrid`'s two setters both derive from
           * the same `grid` prop — so the clear was overwritten and the stale unit survived.
           * `setDivision` owns both fields now; a second call from here would reintroduce
           * exactly the race it replaced.
           */
          onChange={onChangeDivision}
          options={divisionOptions}
          showSearch
          optionFilterProp="label"
          allowClear
          placeholder="Division"
          notFoundContent="No divisions in your scope"
          disabled={disabled}
          loading={isFetching}
          className="w-full"
          size="small"
          aria-label={ariaContext ? `Division, ${ariaContext}` : 'Division'}
        />
      </span>
      <Select
        /* `ANY_UNIT` is the empty string, so an unset unit selects the "Any unit" row. */
        value={unitOrgUnitId ?? ANY_UNIT}
        onChange={(next: string | undefined) => onChangeUnit(next === ANY_UNIT ? undefined : next)}
        options={unitOptions}
        showSearch
        optionFilterProp="label"
        /* ⚠️ NO `allowClear` ANY MORE, and that is the point of the option above: "Any unit"
           is now a row you choose, not an absence you produce by dismissing the field. */
        // ⚠️ Rule 1: no division, no units to choose between.
        /*
         * ⚠️ `disabled === true || !chosen`, NEVER `disabled ?? !chosen`. `??` falls back
         * only on null/undefined, and the grid passes `readOnly` — which is `false` in the
         * ordinary case, so the cascade would never have engaged at all.
         */
        disabled={disabled === true || !chosen}
        placeholder={chosen ? 'Any unit' : 'Choose a division first'}
        notFoundContent={chosen ? 'No units under this division' : 'Choose a division first'}
        className="w-full"
        size="small"
        aria-label={ariaContext ? `Unit, ${ariaContext}` : 'Unit'}
        data-testid="source-unit"
      />
    </div>
  );
};
